import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import styled from "styled-components";
import tw from "twin.macro";

import { Input, InputProps } from "./Input";
import {
  StyledPasswordIndicator,
  StyledPasswordIndicatorWrapper,
} from "./Styles";
import { useForkRef } from "../../utils";
import { IconButton } from "../IconButton";
import {
  EyeIcon,
  EyeOffIcon,
  EyeOffSmallIcon,
  EyeSmallIcon,
  InfoIcon,
  InfoSmallIcon,
} from "../Icons";
import { Tooltip } from "../Tooltip";
import { Caption1 } from "../Typography";

const transform = {
  biggest: "big",
  regular: "small",
  big: "medium",
} as const;

export interface ValidationProps {
  /** Strength calculating function */
  getStrength: (input: string) => 0 | 1 | 2 | 3 | 4;
  /** Array of strength level strings */
  levels: [string, string, string, string, string];
}

export interface InputPasswordProps extends Omit<InputProps, "type" | "size"> {
  /** Input size */
  size?: "regular" | "big" | "biggest";
  /** Renders with shown text */
  show?: boolean;
  /** Removes show/hide control  */
  hideControl?: boolean;
  /** Show tooltip text */
  showTooltipText?: string;
  /** Hide tooltip text */
  hideTooltipText?: string;
  /** Validation object */
  validation?: ValidationProps;
  /** Error message tooltip text */
  errorMessage?: string;
  /** Wrapper className */
  wrapperClassName?: string;
}

const StypedEyeIconSmall = styled(EyeSmallIcon)`
  ${tw`tw-cursor-pointer`}
`;

const StypedEyeOffIconSmall = styled(EyeOffSmallIcon)`
  ${tw`tw-cursor-pointer`}
`;

const StypedEyeIcon = styled(EyeIcon)`
  ${tw`tw-cursor-pointer`}
`;

const StypedEyeOffIcon = styled(EyeOffIcon)`
  ${tw`tw-cursor-pointer`}
`;
const StyledInfoIcon = styled(InfoIcon)`
  ${tw`tw-cursor-help tw-fill-alert tw-mr-1`}
`;

const StyledInfoSmallIcon = styled(InfoSmallIcon)`
  ${tw`tw-cursor-help tw-fill-alert tw-mr-1`}
`;

const StyledInputPasswordWrapper = styled.div`
  width: fit-content;
  display: flex;
  flex-direction: column;
`;

interface InputPasswordAdornmentProps {
  invalid?: boolean;
  errorMessage?: string;
  size: "regular" | "big" | "biggest";
  hideControl?: boolean;
  showText: boolean;
  hideTooltipText: string;
  showTooltipText: string;
  disabled?: boolean;
  toggleShow?: () => void;
}

const InputPasswordAdornment = ({
  invalid,
  errorMessage,
  size,
  hideControl,
  showText,
  hideTooltipText,
  showTooltipText,
  disabled,
  toggleShow,
}: InputPasswordAdornmentProps) => (
  <>
    {invalid && errorMessage && (
      <Tooltip title={errorMessage}>
        {size === "regular" ? <StyledInfoSmallIcon /> : <StyledInfoIcon />}
      </Tooltip>
    )}
    {!hideControl && (
      <Tooltip title={showText ? hideTooltipText : showTooltipText}>
        <IconButton
          variant="text gray"
          type="button"
          size={transform[size] as "big" | "small" | "medium"}
          disabled={disabled}
          onClick={toggleShow}
          style={{ flexShrink: 0 }}
        >
          {showText ? (
            size === "regular" ? (
              <StypedEyeOffIconSmall />
            ) : (
              <StypedEyeOffIcon />
            )
          ) : size === "regular" ? (
            <StypedEyeIconSmall />
          ) : (
            <StypedEyeIcon />
          )}
        </IconButton>
      </Tooltip>
    )}
  </>
);

export const InputPassword = forwardRef<HTMLInputElement, InputPasswordProps>(
  (
    {
      size = "regular",
      disabled,
      show = false,
      hideControl,
      showTooltipText = "Show",
      hideTooltipText = "Hide",
      value: defaultValue = "",
      validation,
      errorMessage,
      invalid,
      onChange,
      wrapperClassName,
      ...rest
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const handleRef = useForkRef(ref, inputRef);

    const [showText, setShowText] = useState(show);
    const [strength, setStrength] = useState(0);
    const [value, setValue] = useState(() => defaultValue as string);

    const handleChange = useCallback(
      (e) => {
        setValue(e.target.value);
        typeof validation?.getStrength === "function" &&
          setStrength(validation.getStrength(e.target.value));
        typeof onChange === "function" && onChange(e.target.value);
      },
      [onChange, validation]
    );

    const toggleShow = useCallback(() => {
      inputRef.current?.focus();
      setShowText((prevState) => !prevState);
    }, []);

    useEffect(() => {
      setShowText(show);
    }, [show]);

    if (
      validation?.levels &&
      validation?.getStrength &&
      typeof validation?.getStrength === "function"
    ) {
      if (validation.levels.length !== 5) {
        console.warn("Levels length must be exactly 5.");
      }
      if (typeof validation.getStrength("test") !== "number") {
        console.warn(
          "getStrength must be a function that accepts a string and returns a number."
        );
      }
    }

    return (
      <StyledInputPasswordWrapper className={wrapperClassName}>
        <Input
          {...rest}
          ref={handleRef}
          size={size}
          value={value}
          disabled={disabled}
          type={showText ? "text" : "password"}
          onChange={handleChange}
          invalid={invalid}
          data-testid="InputPassword"
          endAdornment={
            <InputPasswordAdornment
              invalid={invalid}
              errorMessage={errorMessage}
              size={size}
              hideControl={hideControl}
              showText={showText}
              hideTooltipText={hideTooltipText}
              showTooltipText={showTooltipText}
              disabled={disabled}
              toggleShow={toggleShow}
            />
          }
        />
        {validation?.levels?.length === 5 &&
          typeof validation?.getStrength === "function" &&
          typeof validation?.getStrength("test") === "number" && (
            <>
              <StyledPasswordIndicatorWrapper>
                {[...Array(4)].map((_, index) => (
                  <StyledPasswordIndicator
                    key={index}
                    $strength={strength}
                    $index={index}
                    data-testid={strength >= index + 1 ? "active" : ""}
                  />
                ))}
              </StyledPasswordIndicatorWrapper>
              <Caption1 align="right" color="tertiary">
                {validation.levels[strength]}
              </Caption1>
            </>
          )}
      </StyledInputPasswordWrapper>
    );
  }
);

InputPassword.displayName = "InputPassword";
